/* eslint-disable no-console */
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, Inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { format } from 'date-fns';
import { Observable, first, map, timer } from 'rxjs';

import { Language } from '../environments/environment.model';
import { AboutComponent } from './components/about/about.component';
import { ChatComponent } from './components/chat/chat.component';
import { CountDownComponent } from './components/count-down/count-down.component';
import { CutCardComponent } from './components/cut-card/cut-card.component';
import { GameSelectionComponent } from './components/game-selection/game-selection.component';
import { GameSwitchComponent } from './components/game-switch/game-switch.component';
import { ModalComponent } from './components/modal/modal.component';
import { NotificationComponent } from './components/notification/notification.component';
import { ResultChangeComponent } from './components/result-change/result-change.component';
import { StreamingVideoComponent } from './components/streaming-video/streaming-video.component';
import { EnvironmentResolver } from './core/environment-resolver';
import { DealerServiceToken } from './factories/dealer.factory';
import {
    AppPhase,
    DealerConsoleAction,
    FlagStateType,
    GameType,
    NotificationCode,
    NotificationSeverity,
    TableMode
} from './models/communication/app-enums.model';
import { AppEventType, NewRoundPreparationPhaseChangeEvent, NotificationEvent } from './models/communication/app-events.model';
import { AppState } from './models/communication/app-state.model';
import { CardsChanged } from './models/communication/result-change.model';
import { Notification } from './models/notification.model';
import { YEventPayload } from './models/y-protocol.model';
import { BetsStatusComponent } from './phases/bets-status/bets-status.component';
import { BurnCardsComponent } from './phases/burn-cards/burn-cards.component';
import { ClosedTableComponent } from './phases/closed-table/closed-table.component';
import { ErrorPanelComponent } from './phases/error-panel/error-panel.component';
import { NewRoundPreparationComponent } from './phases/new-round-preparation/new-round-preparation.component';
import { RoundVoidedComponent } from './phases/round-voided/round-voided.component';
import { ShoeChangeComponent } from './phases/shoe-change/shoe-change.component';
import { WaitingForPlayersComponent } from './phases/waiting-for-players/waiting-for-players.component';
import { GameLabelPipe } from './pipes/game-label/game-label.pipe';
import { DealerService } from './services/dealer.service';
import { PluginService } from './services/plugin.service';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        BurnCardsComponent,
        ClosedTableComponent,
        WaitingForPlayersComponent,
        ShoeChangeComponent,
        NewRoundPreparationComponent,
        RoundVoidedComponent,
        ErrorPanelComponent,
        OverlayModule,
        AboutComponent,
        NotificationComponent,
        CutCardComponent,
        ModalComponent,
        BetsStatusComponent,
        GameSwitchComponent,
        GameSelectionComponent,
        GameLabelPipe,
        CountDownComponent,
        ChatComponent,
        StreamingVideoComponent,
        ResultChangeComponent
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent {
    private static readonly originalWidth = 1920;
    private static readonly originalHeight = 1080;
    private static readonly notificationDurationMs = 3000;
    private static readonly gameChangeDelayMs = 50;

    @ViewChild('container') containerEl!: ElementRef<HTMLElement>;

    protected viewportScaling = {
        enabled: true,
        fillWidth: true,
        fillHeight: true
    };

    protected containerHeight = AppComponent.originalHeight;
    protected containerWidth = AppComponent.originalWidth;

    protected appIsHidden = false;
    protected aboutDialogVisible = false;
    protected selectGameVisible = false;
    protected menuVisible = false;
    protected gameSwitchEnabled = true;

    protected notification: Notification = {
        show: false,
        text: '',
        severity: NotificationSeverity.Error,
        timeoutId: undefined,
        duration: AppComponent.notificationDurationMs,
        canClose: false
    };

    protected readonly AppPhase = AppPhase;
    protected readonly TableMode = TableMode;

    protected time$: Observable<string> = this.createTimeObservable();
    protected state$!: Observable<AppState>;
    protected version: string;
    protected languages: Language[] = [];

    protected isVideoHidden = false;
    protected supervisorMode = false;
    protected resultChangeMode = false;

    constructor(
        private readonly environment: EnvironmentResolver,
        private readonly route: ActivatedRoute,
        private readonly pluginService: PluginService,
        private readonly destroyRef: DestroyRef,
        @Inject(DealerServiceToken) private readonly dealerService: DealerService
    ) {
        const { version, languages } = this.environment.getEnvironment();
        this.version = version;
        this.languages = languages || [];

        this.listenToDealerService();
        this.dealerService.start();
    }

    listenToDealerService(): void {
        this.state$ = this.dealerService.getStateObservable();
        this.state$.pipe(first()).subscribe(() => {
            // Timeout because - TypeError: Cannot read properties of undefined (reading 'nativeElement')
            if (this.viewportScaling.enabled) {
                setTimeout(() => {
                    this.scaleViewport(window.innerWidth, window.innerHeight);
                });
            }
        });

        this.dealerService
            .getEventsObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                this.handleDealerEvent(event);
                this.callPlugins(event);
            });
    }

    async onFlagGameClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.RoundFlagged);
    }

    async onFreeGameClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.RoundBetsFree);
    }

    async onCallSupervisorClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.SupervisorCalled);
    }

    async onCloseTableClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.TableClosingRequested);
    }

    async onEnableChatClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.ChatEnabled);
    }

    async onDisableChatClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.ChatEnabled, false);
    }

    async onShoeChangedClick(button: HTMLButtonElement): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            await this.dealerService.sendDealerActionRequest(DealerConsoleAction.ShoeChanged);
        } catch (error) {
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.showNotification($localize`Action failed`, NotificationSeverity.Error, true);
        }
    }

    async onVoidClick(button: HTMLButtonElement): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            await this.dealerService.sendDealerActionRequest(DealerConsoleAction.ChangeShoe);
        } catch (error) {
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.showNotification($localize`Action failed`, NotificationSeverity.Error, true);
        }
    }

    async onRedrawCardsClick(button: HTMLButtonElement): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            // Setting gameSwitchEnabled to false and then to true
            // (in the next tick) effectively restarts the component.
            this.gameSwitchEnabled = false;
            await this.dealerService.sendDealerActionRequest(DealerConsoleAction.RedrawCards);
            this.gameSwitchEnabled = true;
            this.setButtonDisabledState(button, false);
        } catch (error) {
            this.gameSwitchEnabled = true;
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.showNotification($localize`Action failed`, NotificationSeverity.Error, true);
        }
    }

    async onResultChangeConfirm(data: CardsChanged): Promise<void> {
        try {
            await this.dealerService.sendResultChangeRequest(data);
            this.resultChangeMode = false;
        } catch (error) {
            console.error(error);
            this.showNotification($localize`Action failed`, NotificationSeverity.Error, true);
        }
    }

    async onGameSelect(game: GameType): Promise<void> {
        try {
            this.appIsHidden = true;
            await this.dealerService.sendChangeGameRequest(game);
            setTimeout(() => (this.appIsHidden = false), AppComponent.gameChangeDelayMs);
        } catch (error) {
            this.appIsHidden = false;
            console.error(error);
            this.showNotification($localize`Action failed`, NotificationSeverity.Error, true);
        }
    }

    protected scaleViewport(innerWidth: number, innerHeight: number): void {
        const originalProportion = AppComponent.originalWidth / AppComponent.originalHeight;

        const windowProportion = innerWidth / innerHeight;
        const proportionRatio = windowProportion / originalProportion;

        const scale = proportionRatio < 1 ? innerWidth / AppComponent.originalWidth : innerHeight / AppComponent.originalHeight;

        if (this.viewportScaling.fillWidth) {
            this.containerWidth = Math.max(proportionRatio, 1) * AppComponent.originalWidth;
        }

        if (this.viewportScaling.fillHeight) {
            this.containerHeight = AppComponent.originalHeight + AppComponent.originalHeight * (1 - Math.min(proportionRatio, 1));
        }

        this.containerEl.nativeElement.style.transform = `scale(${scale})`;
        this.containerEl.nativeElement.style.width = `${this.containerWidth}px`;
        this.containerEl.nativeElement.style.height = `${this.containerHeight}px`;
    }

    protected scaleContainer(event: UIEvent): void {
        if (this.viewportScaling.enabled) {
            this.scaleViewport((event.target as Window).innerWidth, (event.target as Window).innerHeight);
        }
    }

    protected showNotification(text = 'Error', severity = NotificationSeverity.Error, canClose = false): void {
        if (this.notification.timeoutId) {
            clearTimeout(this.notification.timeoutId);
        }

        this.notification = {
            ...this.notification,
            show: true,
            text,
            severity,
            canClose
        };

        if (!canClose) {
            this.notification.timeoutId = setTimeout(() => this.hideNotification(), this.notification.duration);
        }
    }

    protected hideNotification(): void {
        this.notification = {
            ...this.notification,
            show: false
        };
    }

    protected changeLanguage(languageCode: string): void {
        window.location.href = `/${languageCode}/${window.location.search}`;
    }

    private handleDealerEvent(event: YEventPayload<AppEventType, unknown>): void {
        if (event.name === AppEventType.Notification) {
            this.handleNotification(event.data as NotificationEvent);
            return;
        }

        // Make sure any remaining notification is hidden in `NewRoundPreparation` phase.
        if (
            event.name === AppEventType.GamePhaseChange &&
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            (event.data as NewRoundPreparationPhaseChangeEvent).phase === AppPhase.NewRoundPreparation
        ) {
            this.hideNotification();
        }
    }

    private getNotificationText(code: NotificationCode): string {
        switch (code) {
            case NotificationCode.AuthenticationFailed:
                return $localize`Authentication failed!`;
            case NotificationCode.AuthenticationSuccess:
                return $localize`Successfully authenticated!`;
            case NotificationCode.CroupierChangeInvalidState:
                return $localize`Croupier change not allowed in this game state.`;
            case NotificationCode.DuplicateCard:
                return $localize`Same round, same card detected.`;
            case NotificationCode.FlagFailed:
                return $localize`Flag failed, round not marked.`;
            case NotificationCode.InvalidCard:
                return $localize`Invalid card or card not recognized.`;
            case NotificationCode.InvalidCroupierId:
                return $localize`Invalid croupier ID for this table!`;
            case NotificationCode.ScannerDisconnected:
                return $localize`Card scanner is disconnected!`;
            case NotificationCode.ScannerConnected:
                return $localize`Card scanner is connected and ready.`;
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown notification code: ${code as string}`);
                return code;
        }
    }

    private async onActionClick(button: HTMLButtonElement, flag: FlagStateType, state = true): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            await this.dealerService.sendSetFlagStateRequest(flag, state);
            if (flag === FlagStateType.ChatEnabled) {
                this.setButtonDisabledState(button, false);
            }
        } catch (error) {
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.showNotification($localize`Action failed`, NotificationSeverity.Error, true);
        }
    }

    private setButtonDisabledState(button: HTMLButtonElement, disabled: boolean): void {
        // eslint-disable-next-line no-param-reassign
        button.disabled = disabled;
    }

    private createTimeObservable(): Observable<string> {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return timer(0, 1000).pipe(map(() => format(new Date(), 'HH:mm:ss')));
    }

    private handleNotification(event: NotificationEvent): void {
        const canClose = event.severity !== NotificationSeverity.Info;
        this.showNotification(this.getNotificationText(event.code), event.severity, canClose);
    }

    private callPlugins(event: YEventPayload<AppEventType, unknown>): void {
        for (const plugin of this.pluginService.plugins) {
            plugin(this.dealerService.getState(), event, this.containerEl.nativeElement);
        }
    }
}
